import React, { useEffect, useRef, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaceIcon from '@mui/icons-material/Place';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import styles from './LandingPageV4.module.css';
import quickveeLogo from '../../Assets/Store/quickveeLogo.svg';
import {
  setAuthUser,
  setAuthentication,
  setGuestLogin,
} from '../../redux/features/Authentication/Customer/authUserSlice';
import { flushChatbotOnLogout } from '../../utils/flushChatbotOnLogout';

const accountMenuItems = [
  { label: 'Your Orders', path: '/order/order-history', icon: DescriptionIcon },
  { label: 'Account Settings', path: '/order/profile', icon: SettingsIcon },
  { label: 'Addresses', path: '/order/address', icon: PlaceIcon },
  { label: 'Payment Methods', path: '/order/paymentmethodzone', icon: CreditCardIcon },
  { label: 'Store Credits', path: '/order/storecreditzone', icon: AccountBalanceWalletIcon },
  { label: 'Gift Cards', path: '/order/giftcardzone', icon: CardGiftcardIcon },
  { label: 'Notification Settings', path: '/order/notifications', icon: NotificationsIcon },
  { label: 'Loyalty Points', path: '/order/loyaltypointzone', icon: EmojiEventsIcon },
];

const Header = ({ onSignInClick, onSignUpClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, UserData } = useSelector((state) => state.authUser);
  const firstName =
    UserData?.f_name || UserData?.firstname || UserData?.name || 'User';
  const email = UserData?.email || '';
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const profileTriggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (!isProfileMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsProfileMenuOpen(false);
        profileTriggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isProfileMenuOpen]);

  const handleNavigateFromMenu = (path) => {
    setIsProfileMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setIsProfileMenuOpen(false);
    localStorage.removeItem('userLoginData');
    localStorage.removeItem('is_guest');
    Cookies.remove('userLoginData', { path: '/' });
    Cookies.remove('is_guest', { path: '/' });
    flushChatbotOnLogout(dispatch);
    dispatch(setAuthUser(null));
    dispatch(setGuestLogin(''));
    dispatch(setAuthentication(false));
    navigate('/', { replace: true });
  };

  return (
    <header className={styles.header}>
      <div className={styles.innerWrapper}>
        <button
          type="button"
          className={styles.logoButton}
          onClick={() => navigate('/')}
          aria-label="Quickvee home"
        >
          <img
            src={quickveeLogo}
            alt="Quickvee"
            className={styles.logo}
            width="314"
            height="94"
          />
        </button>

        <nav className={styles.headerNav} aria-label="Landing page">
          <a className={styles.headerNavLink} href="#shop-by-category">
            Browse
          </a>
          <a className={styles.headerNavLink} href="#how-it-works">
            How it works
          </a>
          <a className={styles.headerNavLink} href="#for-merchants">
            For stores
          </a>
        </nav>

        <div className={styles.headerButtons}>
          {isAuthenticated ? (
            <div className={styles.profileMenuWrapper} ref={profileMenuRef}>
              <button
                ref={profileTriggerRef}
                type="button"
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                className={styles.profileBtn}
                aria-label="Open profile menu"
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
                aria-controls="landing-profile-menu"
              >
                <PersonIcon className={styles.iconSm} />
              </button>

              {isProfileMenuOpen && (
                <div
                  id="landing-profile-menu"
                  className={styles.profileMenu}
                  role="menu"
                >
                  <div className={styles.profileHeader}>
                    <p className={styles.profileName}>{firstName}</p>
                    <p className={styles.profileEmail}>{email}</p>
                  </div>
                  <ul className={styles.menuList}>
                    {accountMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.path} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => handleNavigateFromMenu(item.path)}
                            className={styles.menuItemBtn}
                          >
                            <Icon className={styles.iconSm} />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className={styles.menuDivider}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className={styles.logoutBtn}
                    >
                      <LogoutIcon className={styles.iconSm} />
                      <span>Log out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                className={styles.signInBtn}
                onClick={onSignInClick}
              >
                <PersonIcon sx={{ fontSize: 16 }} aria-hidden="true" />
                Sign In
              </button>
              <button
                type="button"
                className={styles.signUpBtn}
                onClick={onSignUpClick}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
